import { matchPath } from "react-router-dom";
import { orgHomePath } from "@shared/appUrls";

/**
 * Route patterns for the app shell: the single source for every `<Route path>`
 * in `src/App.tsx` and for code that matches against those routes.
 *
 * Org-scoped pages live under `/orgs/:orgId` (`ORG_PAGE_ROUTES`), so a URL
 * names the organization it belongs to and App selects that org from it.
 * `APP_ROUTES` holds only the routes outside any org. `LEGACY_PAGE_ROUTES`
 * keeps the pre-org patterns of the org pages so old links can still be
 * recognised and resolved into an org. Captured URL segments are decoded
 * exactly once here; a direct `matchPath` only turns `%2F` into `/` (only
 * `useParams` decodes).
 */
export const APP_ROUTES = {
  requestAccess: "/request-access",
  adminDashboard: "/admin-dashboard",
  adminAnnotations: "/admin-dashboard/annotations",
  adminAnnotationsNew: "/admin-dashboard/annotations/new",
  adminAnnotationTask: "/admin-dashboard/annotations/:orgId/:taskId",
  annotationTaskInShell: "/annotation-tasks/:taskId",
  annotationTaskStandalone: "/annotation-tasks/:orgId/:taskId",
} as const;

export const ORG_ROUTE_PREFIX = "/orgs/:orgId";

/** The org pages' patterns before the org prefix: what old links look like, and each page's org-relative subpath. */
export const LEGACY_PAGE_ROUTES = {
  home: "/",
  traits: "/traits",
  team: "/team",
  settings: "/settings",
  scouting: "/scouting",
  trial: "/trials/:trialId",
  experiment: "/trials/:trialId/experiments/:experimentId",
  task: "/trials/:trialId/experiments/:experimentId/tasks/:taskId",
  taskAiTraits: "/trials/:trialId/experiments/:experimentId/tasks/:taskId/ai-traits",
} as const;

export type OrgPage = keyof typeof LEGACY_PAGE_ROUTES;

export const ORG_PAGE_ROUTES = {
  home: ORG_ROUTE_PREFIX,
  traits: `${ORG_ROUTE_PREFIX}${LEGACY_PAGE_ROUTES.traits}`,
  team: `${ORG_ROUTE_PREFIX}${LEGACY_PAGE_ROUTES.team}`,
  settings: `${ORG_ROUTE_PREFIX}${LEGACY_PAGE_ROUTES.settings}`,
  scouting: `${ORG_ROUTE_PREFIX}${LEGACY_PAGE_ROUTES.scouting}`,
  trial: `${ORG_ROUTE_PREFIX}${LEGACY_PAGE_ROUTES.trial}`,
  experiment: `${ORG_ROUTE_PREFIX}${LEGACY_PAGE_ROUTES.experiment}`,
  task: `${ORG_ROUTE_PREFIX}${LEGACY_PAGE_ROUTES.task}`,
  taskAiTraits: `${ORG_ROUTE_PREFIX}${LEGACY_PAGE_ROUTES.taskAiTraits}`,
} as const satisfies Record<OrgPage, string>;

/** Decodes one URL segment, keeping it as-is when it is not valid percent-encoding (as React Router does). */
function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/** Case-insensitive because React Router's `<Route path>` matching is. */
const ORG_PATH = /^\/orgs\/([^/]+)(\/.*)?$/i;

/**
 * Splits an org URL's pathname into its decoded org ID and the org-relative
 * subpath, which stays encoded: `""` for the org home, otherwise starting with `/`.
 * Returns null for anything that is not `/orgs/{orgId}[/...]`.
 */
export function splitOrgPath(pathname: string): { orgId: string; subpath: string } | null {
  const match = ORG_PATH.exec(pathname);
  if (!match) {
    return null;
  }
  const rest = match[2] ?? "";
  return { orgId: decodeSegment(match[1]), subpath: /^\/*$/.test(rest) ? "" : rest };
}

/** The org URL for `subpath` (`""` or starting with `/`; a bare `/` means the org home). */
export function withOrg(orgId: string, subpath: string): string {
  return orgHomePath(orgId) + (subpath === "/" ? "" : subpath);
}

/** An old, org-less link's URL in `orgId`, keeping its search and hash. */
export function legacyLinkInOrg(
  orgId: string,
  { pathname, search, hash }: { pathname: string; search: string; hash: string }
): string {
  return withOrg(orgId, pathname) + search + hash;
}

/**
 * Matches an old, org-less link to one of the org pages: the explicit
 * allow-list an old-link redirect may act on. Every other path, including
 * `/request-access`, `/admin-dashboard/*`, `/annotation-tasks/*`, `/orgs/*`
 * and unknown paths, gives null. Captured IDs are decoded exactly once.
 */
export function matchLegacyPagePath(pathname: string): { page: OrgPage; params: Record<string, string> } | null {
  for (const page of Object.keys(LEGACY_PAGE_ROUTES) as OrgPage[]) {
    const match = matchPath(LEGACY_PAGE_ROUTES[page], pathname);
    if (match) {
      // matchPath has already turned `%2F` into `/`, which is exactly what a single decode would do.
      const params = Object.fromEntries(
        Object.entries(match.params).map(([name, value]) => [name, decodeSegment(value ?? "")])
      );
      return { page, params };
    }
  }
  return null;
}

/**
 * The documents, relative to `organizations/{orgId}/`, that must all exist for
 * the org-relative page `subpath` to be valid, shallowest first. Sections are
 * valid in any org (`[]`); an unknown subpath gives null. IDs are decoded once,
 * and one that decodes to contain `/` (never a Firestore ID) gives null.
 */
export function orgDocChainForSubpath(subpath: string): string[] | null {
  const match = matchLegacyPagePath(subpath || "/");
  if (!match) {
    return null;
  }
  const { trialId, experimentId, taskId } = match.params;
  if ([trialId, experimentId, taskId].some((id) => id?.includes("/"))) {
    return null;
  }

  const chain: string[] = [];
  if (trialId) chain.push(`trials/${trialId}`);
  if (experimentId) chain.push(`${chain[0]}/experiments/${experimentId}`);
  if (taskId) chain.push(`${chain[1]}/tasks/${taskId}`);
  return chain;
}
